import { Controller } from "@hotwired/stimulus";
import { application } from "src";

import { addClass, removeClass, toggleClass, getParent, find } from '@fretadao/f-js-dom'
import { isPresent } from '@fretadao/f-js-utils';

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.js";
import "leaflet-providers/leaflet-providers";
import "packs/leaflet.forceZIndex";

application.register("b2b-admin-lines-map", class extends Controller {
  static targets = [
    "map",
    "filterModal",
    "appliedFilters",
    "filterCheckbox",
    "formField",
    "submitButton",
    'loadingIndicator',
    'overlay',
    'searchInput',
    'filterResult'
  ];

  INIT_ZOOM = 14;
  MIN_ZOOM = 10;
  MAX_ZOOM = 18;

  connect() {
    this.initializeMap();
    this.addedLines = {};
    this.initializeAutocomplete();
  }

  initializeMap() {
    this.map = L.map(this.mapTarget, {
      center: [-23.55052, -46.633308],
      zoom: this.INIT_ZOOM,
      zoomControl: false,
      attributionControl: false,
    });

    this.map.options.minZoom = this.MIN_ZOOM;
    this.map.options.maxZoom = this.MAX_ZOOM;

    const roadMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    roadMutant.addTo(this.map);

    L.control.zoom({ position: 'topright' }).addTo(this.map);
  }

  initializeAutocomplete() {
    const sessionToken = this.generateUUID();
    const searchInput = this.searchInputTarget;

    searchInput.addEventListener("input", (event) => {
      const query = event.target.value.trim();

      if (query.length < 3) {
        this.clearDropdown();
        return;
      }

      fetch(`/places/google/autocomplete?query=${query}&sessiontoken=${sessionToken}`)
        .then((response) => response.json())
        .then((results) => this.displayAutocompleteResults(results))
        .catch((error) => console.error("Erro no autocomplete:", error));
    });
  }

  displayAutocompleteResults(results) {
    this.clearDropdown();

    if (!results || results.length === 0) return;

    const dropdown = document.createElement("ul");
    dropdown.className = "absolute bg-white shadow-lg border border-gray-300 mt-2 rounded-lg w-full z-50";
    dropdown.style.top = `${this.searchInputTarget.offsetHeight}px`;
    dropdown.style.left = "0";
    dropdown.style.width = "100%";

    dropdown.innerHTML = results
      .map(
        (result) => `
          <li class="p-2 hover:bg-gray-100 cursor-pointer" data-place-id="${result.place_id}">
            ${result.formatted_address}
          </li>`
      )
      .join("");

    dropdown.addEventListener("click", (event) => {
      const placeId = event.target.getAttribute("data-place-id");
      this.selectAddress(placeId);
    });

    this.searchInputTarget.parentNode.appendChild(dropdown);
  }

  selectAddress(placeId) {
    const sessionToken = this.generateUUID();

    fetch(`/places/google/autocomplete/${placeId}?sessiontoken=${sessionToken}`)
      .then((response) => response.json())
      .then((result) => {
        this.centerMap(result.coordinates);
        this.clearDropdown();
      })
      .catch((error) => console.error("Erro ao buscar endereço:", error));
  }

  centerMap(coordinates) {
    const [lat, lng] = coordinates.split(",");
    this.map.setView([parseFloat(lat), parseFloat(lng)], this.INIT_ZOOM);

    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
    }

    this.searchMarker = L.marker([parseFloat(lat), parseFloat(lng)], {
      icon: new L.DivIcon({
        className: '',
        html: `
      <div style="
        width: 40px;
        height: 40px;
        background: #0052CC;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <i class="fa-solid fa-house" style="color: white; transform: rotate(45deg); font-size: 20px;"></i>
      </div>
    `,
        iconSize: [35, 35],
        iconAnchor: [20, 35],
      }),
    }).addTo(this.map);

  }

  clearDropdown() {
    const dropdown = this.searchInputTarget.parentNode.querySelector("ul");
    if (dropdown) dropdown.remove();
  }

  generateUUID() {
    return crypto.randomUUID();
  }

  openFilterModal() {
    removeClass(this.filterModalTarget, "hidden");
    addClass(this.filterModalTarget, "flex");
  }

  closeFilterModal() {
    addClass(this.filterModalTarget, "hidden");
    removeClass(this.filterModalTarget, "flex");
  }

  buildFilterParams() {
    let params = {};

    this.formFieldTargets.forEach((target) => {
      let values = Array.from(target.selectedOptions).map((option) => option.value);

      if (isPresent(values)) {
        params[target.name] = values;
      }
    });

    return params;
  }

  updateAppliedFilters() {
    const params = this.buildFilterParams();
    const filters = Object.keys(params).map((name) => {
      const value = params[name];
      const field = this.formFieldTargets.find((field) => field.name === name);
      const options = field.selectedOptions;

      let displayField;

      if (options.length > 1) {
        displayField = `${options.length} ${field.dataset.displayField}`;
      } else {
        displayField = options[0].text;
      }

      const displayValue = displayField || value;
      const truncatedValue = this.#truncateText(displayValue, 30);

      return `
        <span
          class="bg-gray-200 px-2 py-1 rounded-full mr-2 inline-flex items-center my-px"
          data-filter-name="${name}"
          data-filter-value="${value}"
          title="${displayValue}"
        >
          ${truncatedValue}
          <button type="button" class="ml-1 text-red-500 font-bold" data-action="b2b-admin-lines-map#removeFilter">
            &#x2715;
          </button>
        </span>
      `;
    });

    this.appliedFiltersTarget.innerHTML = filters.length
      ? filters.join("")
      : "Nenhum filtro aplicado.";
  }

  removeFilter(event) {
    const button = event.currentTarget;
    const parentSpan = getParent(button);

    if (!parentSpan) return;

    const fieldName = parentSpan.dataset.filterName;
    const fieldValue = parentSpan.dataset.filterValue;

    const field = this.#findFilledField(fieldName, fieldValue);

    this.#clearMultipleField(field);

    parentSpan.remove();

    Object.keys(this.addedLines).forEach(lineId => {
      const selectedLine = this.addedLines[lineId];

      this.#removeLineLayer(selectedLine);

      delete this.addedLines[lineId];
    });

    this.#hasFilter() ? this.submitButtonTarget.click() : this.#clearFilterResult();
  }

  toggleLine(event) {
    const lineId = `${event.target.dataset.lineId}-${event.target.dataset.lineDirection}`;
    toggleClass(this.overlayTarget, 'hidden');

    if (event.target.checked) {
      this.getLine(event.target);
    } else {
      const selectedLine = this.addedLines[lineId];
      if (selectedLine) {
        this.#removeLineLayer(selectedLine);
        delete this.addedLines[lineId];
      }

      const filterTag = this.appliedFiltersTarget.querySelector(
        `[data-filter-name="line"][data-filter-value="${lineId}"]`
      );
      if (filterTag) filterTag.remove();

      if (!this.#hasFilter()) {
        this.#clearFilterResult();
      }

      toggleClass(this.overlayTarget, 'hidden');
    }
  }

  getLine(target) {
    fetch(target.dataset.linePath, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(response => response.json())
      .then(data => {
        this.addLineToMap(data, target.dataset.lineDirection);
        toggleClass(this.overlayTarget, 'hidden');
      })
      .catch(() => {
        toggleClass(this.overlayTarget, 'hidden');
      });
  }

  stringToColor(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  }

  addLineToMap(data, direction) {
    const waypoints = direction === 'ingoing' ? data.ingoing_waypoints : data.outgoing_waypoints;
    const encoded = direction === 'ingoing' ? data.encoded_ingoing_polyline : data.encoded_outgoing_polyline;

    let latlngs;

    if (encoded) {
      latlngs = L.PolylineUtil.decode(encoded);
    } else if (waypoints && waypoints.length > 0) {
      latlngs = waypoints.map(wp => [wp.latitude, wp.longitude]);
    }

    const lineId = `${data.id}-${direction}`;
    const color = this.stringToColor(lineId);

    const outlineLine = L.polyline(latlngs, {
      color: 'white',
      weight: 4
    }).addTo(this.map);

    const routeLine = L.polyline(latlngs, {
      color: color,
      weight: 2,
      opacity: 0.8
    }).addTo(this.map);

    let waypointMarkers = [];
    this.map.fitBounds(routeLine.getBounds());

    if (waypoints && waypoints.length > 0) {
      waypoints.forEach((waypoint, index) => {
        const pointNumber = index + 1;
        const iconHtml = `
          <div class="leaflet-marker-icon rounded-full text-white text-center border-white border-4 drop-shadow-lg"
            style="width: 25px; height: 25px; display: flex; align-items: center; justify-content: center; background-color: ${color};">
            ${pointNumber}
          </div>
        `;

        const icon = new L.DivIcon({
          html: iconHtml,
          className: '',
          iconSize: [25, 25],
          iconAnchor: [12.5, 12.5]
        });

        const waypointMarker = L.marker([waypoint.latitude, waypoint.longitude], { icon: icon }).addTo(this.map);

        const popupContent = `
          <div>
            <p title="${data.name}">${this.#truncateText(data.name, 30)}</p>
            <p>Horário do ponto: ${waypoint.official_schedule}</p>
            <p>Endereço do ponto: ${waypoint.formatted_address}</p>
          </div>
        `;

        waypointMarker.bindPopup(popupContent);
        waypointMarkers.push(waypointMarker);
      });
    }

    this.addedLines[lineId] = {
      outline: outlineLine,
      polyline: routeLine,
      markers: waypointMarkers
    };
  }

  clearAddedLines() {
    Object.keys(this.addedLines).forEach(lineId => {
      const selectedLine = this.addedLines[lineId];
      this.#removeLineLayer(selectedLine);
    });
    this.addedLines = {};
  }

  clearSearch() {
    this.searchInputTarget.value = '';

    if (this.searchMarker) {
      this.map.removeLayer(this.searchMarker);
      this.searchMarker = null;
    }

    this.clearDropdown();
  }

  selectAllDirection(event) {
    const isChecked = event.target.checked;
    const direction = event.target.dataset.direction;

    const checkboxes = document.querySelectorAll(`.select-line[data-line-direction="${direction}"]`);

    checkboxes.forEach((checkbox) => {
      if (checkbox.checked !== isChecked) {
        checkbox.checked = isChecked;

        if (isChecked) {
          this.getLine(checkbox);
        } else {
          this.toggleLine({ target: checkbox });
        }
      }
    });
  }

  async toggleSelectDirection(event) {
    const isChecked = event.target.checked;
    const direction = event.target.dataset.direction;
    const checkboxes = Array.from(
      document.querySelectorAll(`.select-line[data-line-direction="${direction}"]`)
    );

    this.showLoadingIndicator();

    try {
      if (isChecked) {
        const linePaths = checkboxes
          .filter((checkbox) => !checkbox.checked)
          .map((checkbox) => checkbox.dataset.linePath);

        if (linePaths.length > 0) {
          await this.addLinesToMap(linePaths, direction);
        }

        checkboxes.forEach((checkbox) => (checkbox.checked = true));
      } else {
        checkboxes.forEach((checkbox) => {
          checkbox.checked = false;
          this.toggleLine({ target: checkbox });
        });
      }
    } catch (error) {
      console.error("Erro ao carregar ou remover as linhas:", error);
    } finally {
      this.hideLoadingIndicator();
    }
  }


  async addLinesToMap(linePaths, direction) {
    const batchSize = 5;
    let loadedCount = 0;

    const loadBatch = async (startIndex) => {
      const batch = linePaths.slice(startIndex, startIndex + batchSize);

      const fetchPromises = batch.map((path) =>
        fetch(path, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }).then((response) => response.json())
      );

      try {
        const data = await Promise.all(fetchPromises);
        data.forEach((lineData) => {
          this.addLineToMap(lineData, direction);
          loadedCount++;
          this.updateLoadingProgress(loadedCount, linePaths.length);
        });

        if (startIndex + batchSize < linePaths.length) {
          await loadBatch(startIndex + batchSize);
        }
      } catch (error) {
        console.error("Erro ao carregar as linhas:", error);
      }
    };

    await loadBatch(0);
  }

  showLoadingIndicator() {
    removeClass(this.overlayTarget, "hidden");
    this.overlayTarget.innerHTML = `
    <div class="text-lg font-semibold text-white">
      Carregando linhas...
    </div>
  `;
  }

  updateLoadingProgress(loaded, total) {
    const overlayText = this.overlayTarget.querySelector(".text-lg");
    if (overlayText) {
      overlayText.innerText = `Carregando ${loaded} de ${total} linhas...`;
    }
  }

  hideLoadingIndicator() {
    addClass(this.overlayTarget, "hidden");
  }

  #truncateText(str, maxLength) {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '…';
  }

  #clearFilterResult() {
    this.appliedFiltersTarget.innerHTML = 'Aplique filtros para realizar a busca.';
    this.filterResultTarget.innerHTML = '';
  }

  #findFilledField(fieldName, fieldValue) {
    return this.formFieldTargets.find((field) => {
      return field.name === fieldName && this.#selectedOptionsFor(field) === fieldValue;
    });
  }

  #selectedOptionsFor(field) {
    return Array.from(field.selectedOptions).map(option => option.value).join(',');
  }

  #hasFilter() {
    return this.formFieldTargets.some((field) => field.value != '');
  }

  #removeLineLayer(selectedLine) {
    this.map.removeLayer(selectedLine.polyline);
    this.map.removeLayer(selectedLine.outline);
    selectedLine.markers.forEach(marker => this.map.removeLayer(marker));
  }

  #clearMultipleField(field) {
    const fieldContainer = getParent(field);

    if (field) {
      $(field).val(null).trigger('change'); // clear select2 value

      field.selectedIndex = -1; // disable selected option

      if (fieldContainer) {
        find('.select2-selection__clear', fieldContainer)?.remove(); // remove clear button
      }
    }
  }
});
